import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MobxObjectsManager, useManualRemove } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import HistoryOrderDataState from './history-order-data-state';
import { useTranslation } from 'react-i18next';
import dataAnalysisStore from '../data-analysis-state';
import TableComponent, { HeadCell } from '../../../component/table/table.component';
import i18n from '../../../i18n';
import { HistoryOrderData } from '../../../models/fixed/data-analysis';
import { formatEUR } from '../../../constants/tool.fn';
import { dateFormat, formatEatTypeText } from '../../../constants/tool-order';
import CardView from '../../../component/card/card-view';
import OrderDetailDialog from '../order-data/order-detail-dialog';
import { Order } from '../../../models/order.entity';
import { ErrorMessageBox } from '../data-analysis.page';
import { Box, Typography } from '@mui/material';
import {
    SearchKeyInput,
    type ConditionType,
    type SearchKeyInputItems,
} from '../../../component/form/search-key-input.component';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import GlobalNotification from '../../../component/common/snackbar';
import { ReactComponent as DineIcon } from '../../../static/images/svg/free_breakfast.svg';
import { ReactComponent as DeliveryIcon } from '../../../static/images/svg/ph_handbag-fill.svg';
import './scss/history-order-data.style.scss';

export const historyOrderDataStore = MobxObjectsManager.get(HistoryOrderDataState)

/** 营业分析双卡（堂食/外卖）并排时每列最小宽度（px）；容器放不下两张则第二张换行 */
const DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX = 800;

/** 外卖 `eat_type`，与 `formatEatTypeText` 一致 */
const TAKEOUT_EAT_TYPE_OPTIONS = [0, 1, 2, 3, 5] as const;

/**
 * 历史订单数据页（双卡：堂食/外卖；搜索为本地过滤）
 */
const HistoryOrderDataPage = observer(() => {
    useTranslation();
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    /** 已结堂食：与 `SearchKeyInput` 左选一致（桌台名 / 订单号） */
    const [dineSearchActiveKey, setDineSearchActiveKey] = useState<'table_name' | 'order_head_id'>('table_name');
    /** 堂食搜索框已提交关键词（回车/点搜索/清空后由 `onTextValueChange` 更新），用于本地全字过滤 */
    const [dineOrderInput, setDineOrderInput] = useState('');
    const [dineSearchConditions, setDineSearchConditions] = useState<ConditionType[]>([]);
    /** 已结外卖：与 `SearchKeyInput` 的「左键」一致，决定右侧是搜索框还是类型下拉 */
    const [takeoutSearchActiveKey, setTakeoutSearchActiveKey] = useState<'order_head_id' | 'eat_type'>('order_head_id');
    /** 订单号模式：与 `onTextValueChange` 同步的已提交关键词（回车/点搜索后），用于本地全字过滤 */
    const [takeoutOrderInput, setTakeoutOrderInput] = useState('');
    /** 订单类型模式：由 `SearchKeyInput` 的 onSearch 更新（含 `eat_type` 条件） */
    const [takeoutSearchConditions, setTakeoutSearchConditions] = useState<ConditionType[]>([]);

    const data = historyOrderDataStore.data;
    const startDate = dataAnalysisStore.startDate
    const endDate = dataAnalysisStore.endDate

    const displayedDineOrder = useMemo(() => {
        const list = data.dineOrder ?? [];
        const kw = dineOrderInput.trim().toLowerCase();
        if (!kw) {
            return list;
        }
        if (dineSearchActiveKey === 'table_name') {
            return list.filter(
                (o) => String(o?.table_name ?? '').trim().toLowerCase() === kw
            );
        }
        return list.filter(
            (o) => String(o?.order_head_id ?? '').trim().toLowerCase() === kw
        );
    }, [data.dineOrder, dineSearchActiveKey, dineOrderInput]);
    const displayedTakeoutOrder = useMemo(() => {
        const list = data.takeoutOrder ?? [];
        if (takeoutSearchActiveKey === 'order_head_id') {
            const kw = takeoutOrderInput.trim().toLowerCase();
            if (!kw) {
                return list;
            }
            return list.filter(
                (o) => String(o?.order_head_id ?? '').trim().toLowerCase() === kw
            );
        }
        const typeCond = [...takeoutSearchConditions]
            .reverse()
            .find((c) => c.key === 'eat_type');
        if (typeCond?.value && typeCond.value !== 'all') {
            const t = Number(typeCond.value);
            return list.filter((o) => +(o?.eat_type ?? 0) === t);
        }
        return list;
    }, [data.takeoutOrder, takeoutSearchActiveKey, takeoutOrderInput, takeoutSearchConditions]);

    /** 已结堂食 `SearchKeyInput`：左选桌台名/订单号，右均为搜索框 */
    const dineSearchKeyItems: SearchKeyInputItems[] = useMemo(
        () => [
            {
                value: 'table_name',
                label: i18n.t('business_analysis_page_table_name'),
                type: 'TEXT',
                textPlaceholder: i18n.t('history_order_local_input_table_full_match'),
            },
            {
                value: 'order_head_id',
                label: i18n.t('business_analysis_page_order_account'),
                type: 'TEXT',
                textPlaceholder: i18n.t('history_order_local_input_order_full_match'),
            },
        ],
        [i18n.language]
    );

    /** 已结外卖 `SearchKeyInput` 配置：左选订单号/订单类型，右为搜索框或类型下拉 */
    const takeoutSearchKeyItems: SearchKeyInputItems[] = useMemo(
        () => [
            {
                value: 'order_head_id',
                label: i18n.t('business_analysis_page_order_account'),
                type: 'TEXT',
                textPlaceholder: i18n.t('history_order_local_input_order_full_match'),
            },
            {
                value: 'eat_type',
                label: i18n.t('immediate_order_type'),
                type: 'SELECT',
                options: [
                    { value: 'all', label: i18n.t('business_analysis_page_text_all') },
                    ...TAKEOUT_EAT_TYPE_OPTIONS.map((n) => ({
                        value: String(n),
                        label: formatEatTypeText(n),
                    })),
                ],
            },
        ],
        [i18n.language]
    );

    useEffect(() => {
        historyOrderDataStore.fetchData(stringId, startDate, endDate);
        return () => {
            historyOrderDataStore.setEatInArrSort({}, 'dineOrder');
            historyOrderDataStore.setEatInArrSort({}, 'takeoutOrder');
        }
    }, [stringId, startDate, endDate]);

    useManualRemove(HistoryOrderDataState);

    const getSortStatus = (key: string, id: keyof HistoryOrderData) => {
        const eatInArrSort = historyOrderDataStore.sortKeyValue[id];
        if (key === eatInArrSort?.key) {
            return eatInArrSort.value === 'ASC' ? 2 : 1
        }
        return 0
    }

    const headCellListHisEatIn: HeadCell[] = [
        { id: 'order_head_id', label: i18n.t('business_analysis_page_order_account') },
        { id: 'check_id', label: i18n.t('immediate_order_number') },
        { id: 'table_name', label: i18n.t('business_analysis_page_table_name') },
        { id: 'customer_num', label: i18n.t('immediate_number_people') },
        { id: 'actual_amount', label: i18n.t('immediate_actual_payment'), renderFunction: (item) => formatEUR(+(item?.actual_amount || 0)) },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_table_opening_time'),
            sortStatus: getSortStatus('order_start_time', 'dineOrder'),
            renderFunction: (item) => <div>{dateFormat(item.order_start_time)}</div>
        },
        {
            id: 'order_end_time',
            label: i18n.t('immediate_statement_time'),
            sortStatus: getSortStatus('order_end_time', 'dineOrder'),
            renderFunction: (item) => <div>{dateFormat(item.order_end_time)}</div>
        },
        { id: 'order_employee_name', label: i18n.t('immediate_opener') },
    ];

    const headCellListHisEatOut: HeadCell[] = [
        { id: 'order_head_id', label: i18n.t('business_analysis_page_order_account') },
        {
            id: 'eat_type',
            label: i18n.t('immediate_order_type'),
            renderFunction: (item) => <div>{formatEatTypeText(+(item?.eat_type || 0))}</div>
        },
        { id: 'actual_amount', label: i18n.t('immediate_actual_payment'), renderFunction: (item) => formatEUR(+(item?.actual_amount || 0)) },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_billing_time'),
            sortStatus: getSortStatus('order_start_time', 'takeoutOrder'),
            renderFunction: (item) => <div>{dateFormat(item.order_start_time)}</div>
        },
        {
            id: 'kds_time',
            label: i18n.t('immediate_due_time'),
            sortStatus: getSortStatus('kds_time', 'takeoutOrder'),
            renderFunction: (item) => <div>{item?.kds_time ? dateFormat(item?.kds_time) : ''}</div>
        },
        {
            id: 'order_end_time',
            label: i18n.t('immediate_statement_time'),
            sortStatus: getSortStatus('order_end_time', 'takeoutOrder'),
            renderFunction: (item) => <div>{dateFormat(item.order_end_time)}</div>
        },
        { id: 'order_employee_name', label: i18n.t('immediate_billing_staff') },
    ];

    const cardArr: CardProp[] = [
        { id: 'dineOrder', label: i18n.t('immediate_open_dine_in_order'), data: headCellListHisEatIn },
        { id: 'takeoutOrder', label: i18n.t('immediate_closed_takeaway_orders'), data: headCellListHisEatOut },
    ];

    const handleOpenDetailDialog = (value: Order) => {
        historyOrderDataStore.setOpenOrderDetail(true)
        historyOrderDataStore.setOrderDetailData(value)
        historyOrderDataStore.fetchDetailData()
    }

    const handleSort = (headCell: HeadCell, key: keyof HistoryOrderData) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break;
            case 1:
                sortStatus = 'ASC'
                break;
            case 2:
                sortStatus = ''
                break;
            default:
                sortStatus = ''
                break;
        }
        historyOrderDataStore.setEatInArrSort({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        }, key)
        historyOrderDataStore.setDataSort(key as keyof HistoryOrderData)
    }

    /**
     * 与导出同源：两表经本地筛选后若均无行则提示（与当前表格展示一致）
     * @returns 有任一侧可导为 true
     */
    const preCheckExport = useCallback((): boolean => {
        if (!displayedDineOrder.length && !displayedTakeoutOrder.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [displayedDineOrder.length, displayedTakeoutOrder.length]);

    const handleExportDineAndTakeout = useCallback(
        () =>
            historyOrderDataStore.downloadDineAndTakeoutExcels({
                dineOrder: displayedDineOrder,
                takeoutOrder: displayedTakeoutOrder,
            }),
        [displayedDineOrder, displayedTakeoutOrder]
    );

    const renderCardTitle = (cardId: keyof HistoryOrderData) => {
        const isDineOrder = cardId === 'dineOrder';
        return (
            <Box className='history-order-data-card-title'>
                {isDineOrder ? <DineIcon className='history-order-data-card-icon' /> : <DeliveryIcon className='history-order-data-card-icon' />}
                <span>{isDineOrder ? i18n.t('immediate_open_dine_in_order') : i18n.t('immediate_closed_takeaway_orders')}</span>
            </Box>
        )
    }

    /** 已结堂食：与外卖同一套 `SearchKeyInput`，右侧均为 `SearchComponent` */
    const renderDineLocalSearch = () => (
        <SearchKeyInput
            className='history-order-data-search history-order-data-search-combo'
            items={dineSearchKeyItems}
            showConditionsBox={false}
            conditions={dineSearchConditions}
            onSearch={(next) => setDineSearchConditions([...next])}
            onSearchKeyChange={(key) => {
                setDineSearchActiveKey(key as 'table_name' | 'order_head_id');
                setDineSearchConditions([]);
                setDineOrderInput('');
            }}
            onTextValueChange={(_key, value) => {
                setDineOrderInput(value);
            }}
        />
    );

    /** 已结外卖：复用 `SearchKeyInput`（左选维度、右为输入或下拉，无 chip 区） */
    const renderTakeoutLocalSearch = () => (
        <SearchKeyInput
            className='history-order-data-search history-order-data-search-combo'
            items={takeoutSearchKeyItems}
            showConditionsBox={false}
            conditions={takeoutSearchConditions}
            onSearch={(next) => setTakeoutSearchConditions([...next])}
            onSearchKeyChange={(key) => {
                setTakeoutSearchActiveKey(key as 'order_head_id' | 'eat_type');
                setTakeoutSearchConditions([]);
                setTakeoutOrderInput('');
            }}
            onTextValueChange={(key, value) => {
                if (key === 'order_head_id') {
                    setTakeoutOrderInput(value);
                }
            }}
        />
    );

    return (
        historyOrderDataStore.errorMessage ?
            <ErrorMessageBox errorMessage={historyOrderDataStore.errorMessage} /> :
            <>
                <Box className='history-order-data-top-bar'>
                    <Typography variant='h5' className='history-order-data-page-title'>
                        {i18n.t('history_order_data_page_text_historical_order_data')}
                    </Typography>
                    <Box className='history-order-data-top-actions'>
                        <DataExportIconButton
                            title={i18n.t('tunnel_management_page_export')}
                            disabled={historyOrderDataStore.loading}
                            preCheck={preCheckExport}
                            onExport={handleExportDineAndTakeout}
                        />
                        <PeriodDateFilter
                            startDate={dataAnalysisStore.startDate}
                            endDate={dataAnalysisStore.endDate}
                            pickerValue={dataAnalysisStore.pickerValue}
                            onDateTimeChange={dataAnalysisStore.handleDateTime}
                        />
                    </Box>
                </Box>
                <Box className='history-order-data-page'>
                    <Box
                        className='history-order-data-cards-row'
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        {cardArr.map((cardItem) => {
                            const isDine = cardItem.id === 'dineOrder';
                            const list = isDine ? displayedDineOrder : displayedTakeoutOrder;
                            return (
                                <Box
                                    key={cardItem.id}
                                    className='history-order-data-grid-item'
                                    sx={{
                                        display: 'flex',
                                        flex: `1 1 ${DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX}px`,
                                        minWidth: DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX,
                                        maxWidth: '100%',
                                        minHeight: 0,
                                    }}
                                >
                                    <CardView
                                        title={renderCardTitle(cardItem.id)}
                                        actionRender={cardItem.id === 'dineOrder' ? renderDineLocalSearch() : renderTakeoutLocalSearch()}
                                        className='mt16 history-order-data-card'
                                        style={{
                                            borderRadius: 16,
                                            boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                                            overflow: 'hidden',
                                        }}
                                        contentClassName='history-order-data-card-content'
                                        contentRender={(
                                            <TableComponent
                                                extraClass='history-order-data-table'
                                                isPagination
                                                allCount={list.length}
                                                headCellList={cardItem.data}
                                                list={list}
                                                showChecked={0}
                                                isTableLoading={historyOrderDataStore.loading}
                                                handleSort={(value) => handleSort(value, cardItem.id)}
                                                onRowClick={(row) => handleOpenDetailDialog(row as Order)}
                                            />
                                        )}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                {/* 与「订单」页同一套订单详情弹窗；详情菜品仍走 `get_data_analysis_res_history_order_details`（见 state.fetchDetailData） */}
                <OrderDetailDialog
                    handleClose={() => historyOrderDataStore.setOpenOrderDetail(false)}
                    open={historyOrderDataStore.isOpenOrderDetail}
                    order={historyOrderDataStore.orderDetailSummary}
                    list={historyOrderDataStore.orderDetailDish}
                    isTableLoading={historyOrderDataStore.detailLoading}
                />
            </>
    )
});

export default HistoryOrderDataPage;

type CardProp = {
    id: keyof HistoryOrderData;
    label: string;
    data: HeadCell[]
}
